import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * ValidateLinkOnPaste is an extension for Tiptap that provides a ProseMirror plugin to validate links in pasted content.
 * It parses pasted HTML and checks if any anchor tags contain valid URLs. If an anchor tag does not contain a valid URL,
 * it replaces the anchor tag with its text content.
 *
 * @name ValidateLinkOnPaste
 * @type {Extension}
 * @returns {Extension} A Tiptap extension instance with ProseMirror plugins to validate pasted links.
 *
 * @example
 * import { ValidateLinkOnPaste } from 'path';
 * const editor = new Editor({
 *   content: 'Hello <a href="https://example.com">World</a>!',
 *   extensions: [
 *     ValidateLinkOnPaste,
 *   ],
 * });
 */

export const ValidateLinkOnPaste = Extension.create({
  name: 'ValidateLinkOnPaste',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('ValidateLinkOnPaste'),
        props: {
          transformPastedHTML(html) {
            const dom = new DOMParser().parseFromString(
              html,
              'text/html'
            ).documentElement;
            const aTags = dom?.querySelectorAll('a');
            aTags?.forEach((anchor) => {
              const href = anchor?.getAttribute('href');

              const urlRegex =
                /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i;

              if (!urlRegex.test(href)) {
                const text = document.createTextNode(anchor?.textContent || '');
                anchor.parentNode.replaceChild(text, anchor);
              }
            });
            return dom.innerHTML;
          },
        },
      }),
    ];
  },
});
